/**
 * Delivery Rules API (formerly Routing Rules)
 *
 * Handles all delivery rule-related API calls.
 * Delivery rules determine CRM entity targeting and field updates
 * based on call/chat data conditions using JSONLogic.
 *
 * Renamed from routing_rules in migration 035 for clarity:
 * - "Delivery" = what happens AFTER analysis (CRM field updates)
 * - "InboundRouting" = which PROJECT receives incoming records
 *
 * Primary naming: DeliveryRulesApi (RoutingRulesApi is deprecated)
 */

import { BaseApiClient } from '../base';
import { API_ENDPOINTS } from '../endpoints';
import type {
  EntityTypesResponse,
  ReorderResponse,
  CrmEntityType,
  DeliveryRule,
  DeliveryRulesListResponse,
  DeliveryRuleResponse,
  CreateDeliveryRuleRequest,
  UpdateDeliveryRuleRequest,
  ReorderDeliveryRulesRequest,
} from '../types';

export class DeliveryRulesApi extends BaseApiClient {
  /**
   * Get delivery rules list for organization
   * Optionally filter by projectId
   */
  async getDeliveryRules(params?: {
    projectId?: string;
    limit?: number;
    offset?: number;
  }): Promise<DeliveryRulesListResponse> {
    const query = this.buildQuery(params);
    return this.request<DeliveryRulesListResponse>(
      `${API_ENDPOINTS.DELIVERY_RULES}${query}`
    );
  }

  /**
   * Get single delivery rule by ID
   */
  async getDeliveryRuleById(id: string): Promise<DeliveryRule> {
    const response = await this.request<DeliveryRuleResponse>(
      API_ENDPOINTS.DELIVERY_RULES_BY_ID(id)
    );
    return response.data;
  }

  /**
   * Create new delivery rule
   */
  async createDeliveryRule(data: CreateDeliveryRuleRequest): Promise<DeliveryRule> {
    const response = await this.request<DeliveryRuleResponse>(
      API_ENDPOINTS.DELIVERY_RULES,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    return response.data;
  }

  /**
   * Update delivery rule
   */
  async updateDeliveryRule(
    id: string,
    data: UpdateDeliveryRuleRequest
  ): Promise<DeliveryRule> {
    const response = await this.request<DeliveryRuleResponse>(
      API_ENDPOINTS.DELIVERY_RULES_BY_ID(id),
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );
    return response.data;
  }

  /**
   * Delete delivery rule
   */
  async deleteDeliveryRule(id: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(API_ENDPOINTS.DELIVERY_RULES_BY_ID(id), {
      method: 'DELETE',
    });
  }

  /**
   * Toggle delivery rule enabled status
   */
  async toggleDeliveryRule(id: string): Promise<DeliveryRule> {
    const response = await this.request<DeliveryRuleResponse>(
      API_ENDPOINTS.DELIVERY_RULES_TOGGLE(id),
      {
        method: 'POST',
      }
    );
    return response.data;
  }

  /**
   * Reorder delivery rules (update priorities)
   */
  async reorderDeliveryRules(
    data: ReorderDeliveryRulesRequest
  ): Promise<ReorderResponse> {
    return this.request<ReorderResponse>(API_ENDPOINTS.DELIVERY_RULES_REORDER, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Get available CRM entity types
   */
  async getEntityTypes(): Promise<readonly CrmEntityType[]> {
    const response = await this.request<EntityTypesResponse>(
      API_ENDPOINTS.DELIVERY_RULES_ENTITY_TYPES
    );
    return response.data.map((entityType) => entityType.value);
  }
}
